import { applySnapshot, flow, Instance, SnapshotIn, SnapshotOut, types } from 'mobx-state-tree';
import { AddToCartStatusUpdate } from '@photosi/expo-photosi-sdk/build/Project';
import { PreviewSvgModel } from './previewSvg';
import { IUserImageModel, UserImageModel } from './userImage';
import Datadog from '../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import { logSDKError } from 'src/_shared/logger';
import { DYNAMIC_MODAL_VIEWS } from './dynamic-modal-layer/dynamicModalLayer.constants';
import { PreviewCanvasModel } from './previewCanvas';
import { LookupModel } from './lookup';
import { BaseModel } from './_base/baseModel';
import { PreviewPrintModel } from './previewPrint';
import { PreviewVintageModel } from './previewVintage';
import { ProductCategoryCode } from '@photosi/expo-photosi-sdk/build/Product';
import { UserTextModel } from './userText';
import { parseProductsOptions } from './parsers';
import { ProductOptionModel } from './productOption';
import { PRODUCT_STATUS } from './product.constants';

export type TProductModel = typeof ProductModel;
export type ProductModelInstance = Instance<TProductModel>;
export interface IProductModelIn extends SnapshotIn<TProductModel> {}
export interface IProductModelOut extends SnapshotOut<TProductModel> {}
export interface IProductModel extends ProductModelInstance {}

const ProductChoices = types.model({
  correction: types.optional(types.string, ''),
  format: types.optional(types.string, ''),
  paper: types.optional(types.string, ''),
  passepartout: types.optional(types.string, ''),
});

export const ProductModel = BaseModel.named('Product')
  .props({
    id: types.identifier,
    productConfigurationId: types.string,
    groupId: types.optional(types.string, ''),
    groupCode: types.optional(types.string, ''),
    groupIdSDK: types.optional(types.string, ''),
    groupingInfo: types.optional(types.array(types.string), []),
    orderIndex: types.string,

    type: types.maybe(types.string),
    family: types.maybe(types.string),
    preview: types.union(PreviewSvgModel, PreviewCanvasModel, PreviewPrintModel, PreviewVintageModel),
    smartSelectionTags: types.optional(types.array(types.string), []),
    choices: types.maybe(ProductChoices),

    price: types.optional(types.number, 0),
    quantity: types.optional(types.number, 1),
    formattedPrice: types.optional(types.string, ''),
    lookups: types.optional(types.array(LookupModel), []),
    userImages: types.optional(types.array(UserImageModel), []),
    userTexts: types.optional(types.array(UserTextModel), []),

    status: types.optional(types.enumeration(Object.values(PRODUCT_STATUS)), PRODUCT_STATUS.IDLE),

    // TODO: move to a proper cart store
    progressUploadPercentage: types.optional(types.number, 0),
    progressUploadStatus: types.optional(types.string, ''),
    isCartCancellationEnabled: types.optional(types.boolean, false),
    networkScore: types.optional(types.number, 1),
    cartEta: types.maybe(types.number),

    actualFormat: types.maybe(types.string),
    lookupOptions: types.optional(types.array(ProductOptionModel), []),
  })
  .volatile<{
    isSelected: boolean;
    isUpdating: boolean;
  }>(self => ({
    isSelected: false,
    isUpdating: false,
  }))
  .views(self => ({
    get totalNumberOfUserImages(): number {
      return self.userImages.length;
    },
    get isAnyUserImageUpdating(): boolean {
      return self.preview.isAnyUserImageUpdating;
    },
    get isAnyBlankSpace() {
      return self.preview.isAnyBlankSpace;
    },
    get userImageAreasWithCropAlert() {
      return self.preview.userImageAreasWithCropAlert;
    },
    get isOrientationEnabled(): boolean {
      return [
        ProductCategoryCode.SMALL_SIZE,
        ProductCategoryCode.LARGE_SIZE,
        ProductCategoryCode.SMALL_SIZE_WITH_BORDER,
        ProductCategoryCode.PANORAMIC_SIZE,
      ].includes(self.type as ProductCategoryCode);
    },
    get userImagesWithQualityAlert() {
      return self?.userImages?.reduce((acc: IUserImageModel[], userImageArea: IUserImageModel) => {
        return !!userImageArea.qualityAlert ? [...acc, userImageArea] : acc;
      }, []);
    },
    get userImagesWithCropAlert() {
      return self?.userImages?.reduce((acc: IUserImageModel[], userImageArea: IUserImageModel) => {
        return !!userImageArea.cropAlert ? [...acc, userImageArea] : acc;
      }, []);
    },
  }))
  .actions(self => ({
    syncImageAreasAndImages() {
      self.preview.userImageAreas.forEach(userImageArea => {
        const userImage = self.userImages.find(userImage => userImage.userImageAreaId === userImageArea.id);
        if (!userImage) {
          userImageArea.setUserImage(undefined);
        } else {
          // set userImage
          userImageArea.setUserImage(userImage);
          // sync ImageAreaSizes
          userImage.setImageAreaSizes({ width: userImageArea.width, height: userImageArea.height });
        }
      });
    },
    setIsUpdating(isUpdating: boolean) {
      self.isUpdating = isUpdating;
    },
    setIsSelected(isSelected: boolean) {
      self.isSelected = isSelected;
    },
    setQuantity(quantity: number) {
      self.quantity = quantity;
    },
    setStatus(status: PRODUCT_STATUS) {
      self.status = status;
    },
    syncProductsOptions: flow(function* syncProductsOptions() {
      const options = yield self.sdkProject.getProductsOptions([self.id]);
      const productOptionsParsed = parseProductsOptions(options, self?.family ?? '');
      applySnapshot(self.lookupOptions, productOptionsParsed);
    }),
  }))
  .actions(self => ({
    // TODO: move to a proper cart store
    onCartUpdated({
      progress,
      estimated_remaining_ms,
      status,
      cancellation_enabled,
      network_score,
    }: AddToCartStatusUpdate) {
      if (!!progress) self.progressUploadPercentage = Math.floor(progress);
      if (!!estimated_remaining_ms) self.cartEta = estimated_remaining_ms;
      if (!!network_score) self.networkScore = network_score;
      self.progressUploadStatus = status;
      self.isCartCancellationEnabled = !!cancellation_enabled;
      if (status === 'upload_error') {
        self.root.dynamicModal.activeModal?.setDynamicModalView(DYNAMIC_MODAL_VIEWS.ErrorCart);
      }
    },
  }))
  .actions(self => ({
    beforeDestroy() {
      // Clean up all safeReferences in userImageAreas before destruction
      self.preview.userImageAreas.forEach(userImageArea => {
        userImageArea.setUserImage(undefined);
      });

      if (__DEV__) console.log('[ProductModel] references cleaned before destroy');
    },
  }))
  .actions(self => {
    const remove = flow(function* remove(isOldEditor = false) {
      if (self.status === PRODUCT_STATUS.REMOVING || isOldEditor) {
        self.userImagesWithQualityAlert?.forEach(image => {
          if (image?.productId && self.id === image?.productId) {
            self.root.toastStore.removeToast(`alert-quality-image-toast-${image?.id}`);
          }
        });
        try {
          yield self.sdkProject?.removeProducts([self.id]);
        } catch (error) {
          logSDKError(error, { productId: self.id });
        }
      }
    });
    /**
     * duplicates product
     */
    const duplicate = flow(function* duplicate() {
      try {
        return yield self.sdkProject?.duplicateProduct(self.id);
      } catch (error) {
        logSDKError(error, { productId: self.id });
        throw error;
      }
    });
    /**
     * Change product's configurationId and reloads project
     */
    const applyLookup = flow(function* applyLookup(productConfigurationId: string, groupCode?: string) {
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.configuration_option_apply_time);
      // add opacity
      self.setIsUpdating(true);
      // apply changes on SDK
      return self.sdkProject?.replaceProduct(self.id, productConfigurationId, groupCode).catch(error => {
        logSDKError(error, { productId: self.id, productConfigurationId, groupCode });
      });
    });

    const updateQuantity = flow(function* updateQuantity(quantity: number) {
      self.setQuantity(quantity);
      if (self.quantity === 0) {
        yield remove();
      } else {
        try {
          yield self.sdkProject?.setProductQuantity(self.id, quantity);
        } catch (error) {
          logSDKError(error, { productId: self.id, quantity });
        }
      }
    });
    return {
      remove,
      duplicate,
      applyLookup,
      updateQuantity,
    };
  });
